import { useState, useCallback, useMemo, useRef } from "react";
import type {
  ColumnSizingState,
  OnChangeFn,
  Updater,
} from "@tanstack/react-table";

import { COLUMN_SIZING_STORAGE_KEY_SUFFIX } from "../constants";
import {
  getStorageKey,
  readSizingFromLocalStorage,
  writeSizingToLocalStorage,
} from "../utils";

interface UseColumnSizingPersistenceOptions {
  enabled?: boolean;
  persistToLocalStorage?: boolean;
  columnSizing?: ColumnSizingState;
  onColumnSizingChange?: OnChangeFn<ColumnSizingState>;
  localStorageKeyPrefix?: string;
}

const resolveUpdater = <T>(updaterOrValue: Updater<T>, prev: T): T =>
  typeof updaterOrValue === "function"
    ? (updaterOrValue as (old: T) => T)(prev)
    : updaterOrValue;

export const useColumnSizingPersistence = ({
  enabled = true,
  persistToLocalStorage = false,
  columnSizing: controlledSizing,
  onColumnSizingChange: controlledOnChange,
  localStorageKeyPrefix,
}: UseColumnSizingPersistenceOptions) => {
  const isControlled = controlledSizing !== undefined;

  const storageKey = useMemo(
    () =>
      getStorageKey(localStorageKeyPrefix, COLUMN_SIZING_STORAGE_KEY_SUFFIX),
    [localStorageKeyPrefix]
  );

  const initialSizingRef = useRef<ColumnSizingState | null>(null);
  if (initialSizingRef.current === null) {
    initialSizingRef.current =
      enabled && persistToLocalStorage && !isControlled
        ? readSizingFromLocalStorage(storageKey)
        : {};
  }

  const [internalSizing, setInternalSizing] = useState<ColumnSizingState>(
    initialSizingRef.current
  );

  const sizing = isControlled ? controlledSizing : internalSizing;

  const setColumnSizing = useCallback(
    (updaterOrValue: Updater<ColumnSizingState>) => {
      if (!isControlled) {
        setInternalSizing(prev => resolveUpdater(updaterOrValue, prev));
      }
      controlledOnChange?.(updaterOrValue);
    },
    [isControlled, controlledOnChange]
  );

  const persistUserResizedSizing = useCallback(
    (sizingState: ColumnSizingState, userResizedIds: Set<string>) => {
      if (!enabled || !persistToLocalStorage || isControlled) return;

      const filtered: ColumnSizingState = {};
      userResizedIds.forEach(id => {
        const size = sizingState[id];
        if (typeof size === "number") filtered[id] = size;
      });

      writeSizingToLocalStorage(storageKey, filtered);
    },
    [enabled, persistToLocalStorage, isControlled, storageKey]
  );

  return {
    columnSizing: enabled ? sizing : {},
    setColumnSizing,
    initialUserResizedIds: Object.keys(initialSizingRef.current),
    persistUserResizedSizing,
  };
};
